import { useState } from "react";
import useIsMobile from "./useIsMobile";

const START_DATE = new Date("2026-03-09");

function weekStartDate(weekNum) {
  const d = new Date(START_DATE);
  d.setDate(d.getDate() + (weekNum - 1) * 7);
  return d;
}

function weekEndDate(weekNum) {
  const d = weekStartDate(weekNum);
  d.setDate(d.getDate() + 4);
  return d;
}

function fmtShort(date) {
  return date.toLocaleDateString("en-NZ", { day: "numeric", month: "short" });
}

function weekLabel(weekNum) {
  return `Wk ${weekNum}  ${fmtShort(weekStartDate(weekNum))}`;
}

function phaseDateRange(weekRange) {
  const start = weekStartDate(weekRange[0]);
  const end = weekEndDate(weekRange[1]);
  const startStr = start.toLocaleDateString("en-NZ", { day: "numeric", month: "short" });
  const endStr = end.toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" });
  return `${startStr} – ${endStr}`;
}

const PHASES = [
  {
    id: 0,
    name: "Set Up",
    weeks: "Weeks 1–2",
    weekRange: [1, 2],
    color: "#0ea5e9",
    colorDim: "#0c2033",
    goal: "Shared foundation before sprint work begins",
    description: "Agree the problem statement and what success looks like. Define goals, measures of success, and how we'll govern and report back to the business. Team ways of working established before sprint work begins.",
    outputs: ["Problem statement agreed", "Goals and measures of success documented", "Governance framework and reporting cadence set", "Team kickoff completed"],
    milestone: "Team aligned and ready to sprint by end of Week 2",
    sprints: [
      {
        week: 1,
        title: "Problem & Goals",
        focus: "Align on what we're solving and why",
        tasks: [
          "Draft and agree the problem statement — what is the AI Production Engine solving and for whom",
          "Define goals: what does success look like at 6 months, 12 months, and project close?",
          "Identify measures of success — quantitative and qualitative",
          "Agree the scope boundary: what is in and out of this project",
          "Establish weekly progress update to Group executive team",
        ],
        endState: "Problem statement and goals agreed by the full team.",
      },
      {
        week: 2,
        title: "Governance & Kickoff",
        focus: "How we work and report",
        tasks: [
          "Define governance model: who needs to know what, and how often",
          "Agree reporting cadence and format for executive and board visibility",
          "Establish team ways of working: sprint rhythm, decision rights, escalation path",
          "Full team kickoff — problem statement presented, questions answered, sprint 1 begins",
        ],
        endState: "Governance model agreed. Team kicked off. Ready to sprint.",
      },
    ],
  },
  {
    id: 1,
    name: "Prove It",
    weeks: "Weeks 3–6",
    weekRange: [3, 6],
    color: "#3b82f6",
    colorDim: "#1e3a5f",
    goal: "Demonstrate the pipeline exists",
    description: "Pipeline only — no UI. Journalists submit material directly to JC.One story, one input, one output. TV package first. Ugly is fine. Generate signal about where to focus.",
    outputs: ["First AI-generated TV package draft", "Identified top failure modes", "Intake design brief — channels, classification logic, identity model"],
    milestone: "First output by Friday of Week 4",
    sprints: [
      {
        week: 3,
        title: "Christchurch Kickoff",
        focus: "Team together — ideate, design, plan",
        tasks: [
          "Day 1: Bold ideation with CEO — draw out the ideal end-state platform",
          "Day 2: TV package deep dive — map end-to-end workflow from story to broadcast",
          "Day 3: Map technical architecture, components, integrations, and data flows",
          "Day 4: Plan the first remote sprint — assign workstreams, define success criteria",
        ],
        endState: "Shared vision, architecture mapped, first sprint planned. Team aligned and ready to build.",
      },
      {
        week: 4,
        title: "First Output",
        focus: "Pipeline exists by Friday",
        tasks: [
          "Agree the story — real material, known well by K. or Bl.",
          "Define the TV package schema: anchor intro, reporter VO, grabs, B-roll direction, outro",
          "J. builds the raw pipeline — journalist input in, package draft out",
          "First ugly output reviewed by end of week",
        ],
        endState: "A TV package draft exists. It will be rough. That's the point.",
      },
      {
        week: 5,
        title: "Failure Modes & First Fix",
        focus: "What's broken, fix the worst",
        tasks: [
          "Run two more stories through the pipeline",
          "K. and BE. score each output — what's wrong, what's surprisingly good",
          "Categorise failures: factual, structural, tone, format",
          "J. targets the highest-impact failure mode and ships a fix",
          "Begin designing Intake: channels (email, DM, voice note), identity model, classification logic",
        ],
        endState: "Clear-eyed failure list. Top issue fixed. Intake design underway.",
      },
      {
        week: 6,
        title: "Phase Close-out",
        focus: "Prove It complete",
        tasks: [
          "Run a clean end-to-end test with the best version of the pipeline",
          "Write up what we've learned — this becomes the Phase 2 brief",
          "Estimate projected operating costs of AI Production Engine for Stuff Digital — required input for April / May budget process",
          "Agree the editorial quality bar for Phase 2",
          "Agree the thin slice UI model: every Phase 2+ sprint ships a UI increment alongside pipeline work",
        ],
        endState: "Confidence the pipeline concept is sound. Thin slice principle agreed. Ready to expand.",
      },
    ],
  },
  {
    id: 2,
    name: "Shape It",
    weeks: "Weeks 7–12",
    weekRange: [7, 12],
    color: "#8b5cf6",
    colorDim: "#2d1b5f",
    goal: "All five artefact types working, thin UI in place",
    description: "Thin slice model begins. Every sprint ships a UI increment alongside pipeline work. Intake v1 built. Dashboard prototype is the north star — we're walking toward it week by week.",
    outputs: ["Full artefact suite generating", "Editorial quality baseline set", "Intake v1 — email and DM ingestion live", "Thin dashboard UI tracking toward prototype"],
    milestone: "Journalist submits via Intake and receives output through the UI",
    sprints: [
      {
        week: 7,
        title: "Expand to Article",
        focus: "Second artefact type",
        tasks: [
          "Adapt TV package pipeline for written article output",
          "Define article schema: headline, lede, body, image slots",
          "K. reviews first AI-drafted article against editorial standards",
          "Ship first thin slice UI: a minimal story slug view showing input received and output generated",
        ],
        endState: "AI generates an article draft. A basic UI exists for the first time.",
      },
      {
        week: 8,
        title: "Audio + Short Video",
        focus: "Broadcast formats",
        tasks: [
          "Build audio story output — script for voice synthesis or presenter read",
          "Build short-form vertical video script and shot direction",
          "Build Intake v1 — email and DM ingestion, journalist identity lookup",
          "Bl. stress-tests with a more complex investigation-style input via Intake",
        ],
        endState: "Material submitted via email or DM flows into the pipeline automatically",
      },
      {
        week: 9,
        title: "Chatbot Format",
        focus: "Conversational output",
        tasks: [
          "Define chatbot artefact: Q&A pairs, key facts, follow-up prompts",
          "Build chatbot output generator from story slug",
          "Build Intake classification: new story vs. existing slug — confidence threshold logic",
          "Build human review step: approve routing before content is attached to a slug",
        ],
        endState: "All five artefact types generating. Intake routes material with human confirmation.",
      },
      {
        week: 10,
        title: "Quality Iteration",
        focus: "Raise the bar",
        tasks: [
          "K. and BE.score all five formats against editorial standards",
          "Identify the top three failure modes across all formats",
          "J. targets the highest-impact fix",
          "Re-run the same story — measure improvement",
        ],
        endState: "Clear view of where AI is good enough, where it needs work",
      },
      {
        week: 11,
        title: "Submission Interface",
        focus: "Journalist-facing UX",
        tasks: [
          "Expand dashboard UI to show artefact cards and story timeline",
          "Intake extended to handle voice notes — transcription and routing",
          "End-to-end test: journalist sends voice note via WhatsApp, output appears in dashboard UI",
          "K. and BE.use the full flow without hand-holding — note friction points",
        ],
        endState: "A journalist can submit via messaging and see outputs in the UI — no engineering involvement",
      },
      {
        week: 12,
        title: "Phase Review",
        focus: "Shape It close-out",
        tasks: [
          "Run a full story from raw input to five published-ready artefacts",
          "Score output quality honestly — what's production-ready vs. not yet",
          "Document the repeatable workflow that's emerged",
          "Define the Phase 3 quality bar and hardening priorities",
        ],
        endState: "Clear go/no-go on readiness to harden for broader use",
      },
    ],
  },
  {
    id: 3,
    name: "Harden It",
    weeks: "Weeks 13–20",
    weekRange: [13, 20],
    color: "#f59e0b",
    colorDim: "#3d2800",
    goal: "Small group can use it on real stories",
    description: "Thin slices accumulate into a full product. Intake handles messy real-world inputs. Human-in-the-loop editorial controls defined. Dashboard reaches feature parity with prototype. Quality bar rises.",
    outputs: ["Production-grade Intake — email, DM, voice note", "Dashboard at feature parity with prototype", "Human review flow for Intake routing", "Pilot group of 5–8 journalists"],
    milestone: "First story published via the new system",
    sprints: [
      {
        week: 13,
        title: "Messy Inputs",
        focus: "Real-world submission chaos",
        tasks: [
          "Test Intake with voice notes, images, partial wire copy, incomplete notes",
          "Define minimum viable input — what does the AI actually need to produce good output?",
          "Build Intake validation and graceful degradation — unknown sender queue, low-confidence routing",
          "Document edge cases for future hardening",
        ],
        endState: "Intake handles incomplete or unstructured inputs without breaking",
      },
      {
        week: 14,
        title: "Human-in-the-Loop",
        focus: "Editorial control points",
        tasks: [
          "Define mandatory review checkpoints: Intake routing confirmation and pre-publication approval",
          "Build the routing review UI — approve, reassign, or reject an incoming submission",
          "K. signs off on the editorial governance model",
          "Test the full flow: journalist sends material, reviewer confirms routing, AI processes, editor approves output",
        ],
        endState: "Clear editorial control model — Intake routes, humans confirm, AI generates, editors approve",
      },
      {
        week: 15,
        title: "Dashboard Build",
        focus: "Full interface live",
        tasks: [
          "J. builds dashboard to feature parity with the prototype",
          "Story slug list, artefact cards, timeline, commission flow all working",
          "Internal team uses it daily for real stories",
          "Identify and fix the top UX friction points",
        ],
        endState: "Team is working out of the dashboard, not ad-hoc tools",
      },
      {
        week: 16,
        title: "Quality Sprint",
        focus: "Raise output bar",
        tasks: [
          "Systematic review of output quality across all five formats",
          "Focus on the two weakest formats — targeted improvement",
          "Bl. runs investigative-complexity story through the full system",
          "Set the quality benchmark for pilot group onboarding",
        ],
        endState: "Output quality good enough to show journalists outside the core team",
      },
      {
        week: 17,
        title: "Pilot Onboarding",
        focus: "First external users",
        tasks: [
          "Select 5–8 journalists for the pilot group",
          "Onboarding session — a working session, not a training course",
          "Each pilot journalist submits a real story through the system",
          "Collect feedback systematically — what works, what breaks trust",
        ],
        endState: "Pilot group using the system on real stories with minimal hand-holding",
      },
      {
        week: 18,
        title: "Pilot Feedback Loop",
        focus: "Iterate on real use",
        tasks: [
          "Weekly feedback session with pilot group",
          "J. ships fixes based on the highest-friction issues",
          "First story published via the new system — a milestone worth marking",
          "Document what changed from the prototype assumptions",
        ],
        endState: "First published story. Proof the system works in production.",
      },
      {
        week: 19,
        title: "Stability Sprint",
        focus: "Harden for scale",
        tasks: [
          "Performance and reliability improvements",
          "Error handling and failure recovery",
          "Logging and observability — understand what the system is doing",
          "Prepare the rollout brief for Phase 4",
        ],
        endState: "System stable enough for broader rollout",
      },
      {
        week: 20,
        title: "Phase Review",
        focus: "Harden It close-out",
        tasks: [
          "Pilot group assessment — what's working, what's not",
          "Go/no-go decision on Phase 4 rollout",
          "Rollout plan finalised with K.",
          "Comms to broader newsroom — what's coming",
        ],
        endState: "Confident go decision on full newsroom rollout",
      },
    ],
  },
  {
    id: 4,
    name: "Expand It",
    weeks: "Weeks 21–30",
    weekRange: [21, 30],
    color: "#10b981",
    colorDim: "#0d3326",
    goal: "Naviga feels redundant",
    description: "Controlled rollout across the newsroom. Parallel running with Naviga. Mastheads and Neighbourly onboarded.",
    outputs: ["the newsroom on new system", "Mastheads onboarded", "Neighbourly / Neighbourly News integrated"],
    milestone: "Majority of daily output through new system",
    sprints: [
      {
        week: 21,
        title: "Newsroom Rollout",
        focus: "Full newsroom begins",
        tasks: [
          "All-hands briefing for the newsroom",
          "Parallel running begins — new system alongside Naviga",
          "Dedicated support channel for journalist questions",
          "Daily usage metrics tracked from day one",
        ],
        endState: "the newsroom actively using the system alongside Naviga",
      },
      {
        week: 22,
        title: "Adoption Sprint",
        focus: "Drive usage, reduce friction",
        tasks: [
          "Identify non-adopters and understand blockers",
          "J. ships the top five friction fixes from rollout feedback",
          "K. champions usage in daily editorial meetings",
          "First weekly usage report to leadership",
        ],
        endState: "Adoption curve moving in the right direction",
      },
      {
        week: 23,
        title: "Mastheads Onboarding",
        focus: "The Post, The Press, regionals",
        tasks: [
          "Onboarding sessions with Mastheads editorial leads",
          "Adapt submission flow for Mastheads story types and tone",
          "Test against regional and long-form content",
          "Identify Mastheads-specific requirements",
        ],
        endState: "Mastheads journalists submitting stories through the system",
      },
      {
        week: 24,
        title: "Mastheads Iteration",
        focus: "Fit for print-heritage journalism",
        tasks: [
          "Address Mastheads-specific output quality issues",
          "Tune AI outputs for long-form, in-depth story types",
          "Cross-Group usage report — compare adoption patterns",
          "Flag any print output requirements emerging",
        ],
        endState: "System producing output that Mastheads journalists trust",
      },
      {
        week: 25,
        title: "Neighbourly Integration",
        focus: "Community journalism layer",
        tasks: [
          "Onboard Neighbourly News to the submission pipeline",
          "Test community-submitted inputs — lower editorial complexity, higher volume",
          "Define the moderation and quality threshold for community content",
          "Neighbourly-specific artefact formats if needed",
        ],
        endState: "Neighbourly News running through the same pipeline as core journalism",
      },
      {
        week: 26,
        title: "Cross-Group Quality Review",
        focus: "Consistency across entities",
        tasks: [
          "Review output quality across the newsroom, Mastheads, Neighbourly",
          "Identify format or quality divergence between entities",
          "J. addresses systemic issues affecting multiple groups",
          "K. and BE.assess whether editorial standards are holding",
        ],
        endState: "Consistent quality bar across all three entities",
      },
      {
        week: 27,
        title: "Naviga Dependency Audit",
        focus: "Map what remains",
        tasks: [
          "Audit all remaining Naviga authoring usage",
          "Identify journalists or workflows still dependent on Naviga",
          "Build the migration plan for remaining holdouts",
          "Confirm no authoring functionality gap before decommission",
        ],
        endState: "Clear picture of what remains to migrate",
      },
      {
        week: 28,
        title: "Migration Sprint",
        focus: "Move the holdouts",
        tasks: [
          "1:1 sessions with remaining Naviga-dependent workflows",
          "Build any missing functionality identified in the audit",
          "Target: Naviga authoring usage below 20%",
          "Comms on decommission timeline",
        ],
        endState: "Naviga authoring usage minority and declining",
      },
      {
        week: 29,
        title: "Platform Hardening",
        focus: "Prepare for full load",
        tasks: [
          "Performance testing at full Group-wide usage load",
          "Reliability and uptime improvements",
          "Backup and recovery procedures documented",
          "Ops handoff plan prepared",
        ],
        endState: "System ready to carry full Group publishing load",
      },
      {
        week: 30,
        title: "Phase Review",
        focus: "Expand It close-out",
        tasks: [
          "Cross-Group adoption report",
          "Go/no-go on Phase 5 decommission",
          "Final Naviga authoring migration plan confirmed",
          "November go-live comms prepared",
        ],
        endState: "Ready to decommission Naviga authoring on schedule",
      },
    ],
  },
  {
    id: 5,
    name: "Adopt It",
    weeks: "Weeks 31–38",
    weekRange: [31, 38],
    color: "#ef4444",
    colorDim: "#3d1515",
    goal: "Naviga authoring retired",
    description: "Full newsroom migration. Naviga authoring off. Live by November. Buffer before February contract expiry.",
    outputs: ["Full migration complete", "Naviga authoring decommissioned", "Platform ready for external expansion"],
    milestone: "Live by November 2026 — Naviga off Feb 2027",
    sprints: [
      {
        week: 31,
        title: "Final Migration Push",
        focus: "Zero Naviga authoring",
        tasks: [
          "All remaining Naviga authoring workflows migrated",
          "Final journalist onboarding sessions",
          "Naviga authoring access formally removed",
          "Confirm no content is being created in Naviga",
        ],
        endState: "Zero authoring activity in Naviga",
      },
      {
        week: 32,
        title: "System Stabilisation",
        focus: "Full load, no fallback",
        tasks: [
          "Monitor system under full Group-wide load without Naviga fallback",
          "24-hour on-call coverage during transition week",
          "Address any emergent issues immediately",
          "Daily status report to leadership",
        ],
        endState: "System stable at full production load",
      },
      {
        week: 33,
        title: "Ops Handoff",
        focus: "Sustainable operations",
        tasks: [
          "Document all operational procedures",
          "Handoff to ongoing engineering support model",
          "Monitoring and alerting fully configured",
          "Playbook for common failure scenarios",
        ],
        endState: "System operationally self-sufficient — not dependent on founding team",
      },
      {
        week: 34,
        title: "November Go-Live",
        focus: "Official launch",
        tasks: [
          "Internal announcement — system is now the platform of record",
          "External comms if appropriate",
          "Mark the milestone with the team",
          "Begin platform expansion planning",
        ],
        endState: "AI Production Engine live. Naviga authoring retired.",
      },
      {
        week: 35,
        title: "Post-Launch Review",
        focus: "What did we learn",
        tasks: [
          "Full retrospective with the founding team",
          "Document what worked, what we'd do differently",
          "Assess output quality vs. original ambition",
          "Identify the top three improvements for the next phase",
        ],
        endState: "Institutional knowledge captured — ready for next phase of platform growth",
      },
      {
        week: 36,
        title: "External Platform Planning",
        focus: "External expansion",
        tasks: [
          "Define the proposition for external media companies",
          "Identify first candidate partner organisations",
          "Assess what's needed to productise for external use",
          "Brief to leadership on commercial opportunity",
        ],
        endState: "Clear view of what it takes to take this to market externally",
      },
      {
        week: 37,
        title: "Naviga Contract Prep",
        focus: "February expiry readiness",
        tasks: [
          "Confirm full Naviga authoring decommission",
          "Ads and Print Naviga tracks confirmed as separate project",
          "Contract expiry documentation prepared",
          "Any final data migration or archiving completed",
        ],
        endState: "Ready for clean Naviga contract expiry in February 2027",
      },
      {
        week: 38,
        title: "Close-out",
        focus: "AI Production Engine complete",
        tasks: [
          "Final project documentation",
          "Team recognition and transition",
          "Platform handed to product and engineering for ongoing development",
          "AI Foundations Team mandate refreshed for next chapter",
        ],
        endState: "AI Production Engine complete. The AI Foundations Team moves on to the next thing.",
      },
    ],
  },
];

const TEAM = [
  {
    name: "Ben",
    role: "Product Manager",
    avatar: "BH",
    color: "#f97316",
    capacity: [
      { weekRange: [1, 2], pct: 100, note: "Project setup — problem statement, goals, governance" },
      { weekRange: [3, 6], pct: 60, note: "Hands-on — setting direction, unblocking" },
      { weekRange: [7, 12], pct: 40, note: "Weekly sprint reviews, stakeholder comms" },
      { weekRange: [13, 20], pct: 30, note: "Quality bar, pilot oversight" },
      { weekRange: [21, 30], pct: 25, note: "Rollout leadership, exec reporting" },
      { weekRange: [31, 38], pct: 20, note: "Migration sign-off, Naviga decom" },
    ],
  },
  {
    name: "Justis",
    role: "Engineer",
    avatar: "JC",
    color: "#06b6d4",
    capacity: [
      { weekRange: [1, 2], pct: 50, note: "Project setup — technical scoping" },
      { weekRange: [3, 6], pct: 100, note: "Full-time — pipeline architecture & build" },
      { weekRange: [7, 12], pct: 100, note: "Full-time — artefact expansion, interface v1" },
      { weekRange: [13, 20], pct: 100, note: "Full-time — hardening, dashboard build" },
      { weekRange: [21, 30], pct: 80, note: "Rollout support, integration work" },
      { weekRange: [31, 38], pct: 60, note: "Migration, decom support, next platform" },
    ],
  },
  {
    name: "Keith",
    role: "Editor in Chief",
    avatar: "KL",
    color: "#ec4899",
    capacity: [
      { weekRange: [1, 2], pct: 50, note: "Project setup — editorial requirements input" },
      { weekRange: [3, 6], pct: 30, note: "Story selection, first-output editorial review" },
      { weekRange: [7, 12], pct: 40, note: "Artefact quality judging — key sprint moments" },
      { weekRange: [13, 20], pct: 30, note: "Pilot group lead, newsroom comms" },
      { weekRange: [21, 30], pct: 20, note: "Rollout champion — the newsroom" },
      { weekRange: [31, 38], pct: 15, note: "Migration oversight" },
    ],
  },
  {
    name: "Blair",
    role: "Editor",
    avatar: "BE",
    color: "#a78bfa",
    capacity: [
      { weekRange: [1, 2], pct: 30, note: "Project setup — editorial requirements input" },
      { weekRange: [3, 6], pct: 30, note: "Complex story input — stress test the pipeline" },
      { weekRange: [7, 12], pct: 40, note: "Editorial quality bar — investigative standards" },
      { weekRange: [13, 20], pct: 25, note: "Pilot user, edge case testing" },
      { weekRange: [21, 30], pct: 15, note: "Mastheads onboarding support" },
      { weekRange: [31, 38], pct: 10, note: "Advisory" },
    ],
  },
];

const TOTAL_WEEKS = 38;

function CapacityBar({ pct, color }) {
  return (
    <div style={{ position: "relative", height: 20, background: "#0f1623", borderRadius: 3, overflow: "hidden", border: "1px solid #1a2438" }}>
      <div style={{
        position: "absolute", left: 0, top: 0, bottom: 0,
        width: `${pct}%`, background: color,
        opacity: pct >= 80 ? 1 : pct >= 50 ? 0.75 : pct >= 30 ? 0.5 : 0.3,
        borderRadius: 2,
      }} />
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", paddingLeft: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: pct >= 50 ? "#fff" : "#64748b", fontFamily: "'IBM Plex Mono', monospace" }}>{pct}%</span>
      </div>
    </div>
  );
}

function SprintGrid({ phase, mob }) {
  const count = phase.sprints.length;
  const cols = mob ? 1 : count <= 4 ? count : count <= 6 ? 3 : 4;
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12 }}>
      {phase.sprints.map((sprint) => (
        <div key={sprint.week} style={{
          background: "#070d1a",
          border: `1px solid ${phase.colorDim}`,
          borderRadius: 8,
          padding: "14px 16px 16px",
          display: "flex",
          flexDirection: "column",
        }}>
          <div style={{ marginBottom: 8 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, color: phase.color,
              fontFamily: "'IBM Plex Mono', monospace",
              background: phase.colorDim, padding: "2px 6px", borderRadius: 3,
            }}>WK {sprint.week}  ·  {fmtShort(weekStartDate(sprint.week))}</span>
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0", marginBottom: 2, lineHeight: 1.3 }}>{sprint.title}</div>
          <div style={{ fontSize: 11, color: phase.color, fontWeight: 600, marginBottom: 10, opacity: 0.8 }}>{sprint.focus}</div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5, marginBottom: 12 }}>
            {sprint.tasks.map((task, i) => (
              <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                <span style={{ color: phase.color, fontSize: 11, marginTop: 2, flexShrink: 0, opacity: 0.4 }}>·</span>
                <span style={{ fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>{task}</span>
              </div>
            ))}
          </div>
          <div style={{ borderTop: "1px solid #1e2d42", paddingTop: 10, marginTop: "auto" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4, fontFamily: "'IBM Plex Mono', monospace" }}>End state</div>
            <div style={{ fontSize: 12, color: phase.color, lineHeight: 1.5, fontStyle: "italic", opacity: 0.85 }}>{sprint.endState}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SprintPlan() {
  const [hoveredPhase, setHoveredPhase] = useState(null);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [tooltip, setTooltip] = useState(null); // { text, x, y }
  const mob = useIsMobile();

  const active = selectedPhase || hoveredPhase;
  const activePhase = active !== null ? PHASES.find(p => p.id === active) : null;

  return (
    <div
      style={{
        fontFamily: "'IBM Plex Sans', 'Helvetica Neue', sans-serif",
        background: "#070d1a",
        color: "#e2e8f0",
        minHeight: "100vh",
        padding: mob ? "20px 16px" : "40px 48px",
        position: "relative",
      }}
      onMouseMove={e => tooltip && setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
    >
      {tooltip && (
        <div style={{
          position: "fixed",
          left: tooltip.x + 12,
          top: tooltip.y - 36,
          background: "#1e2d42",
          border: "1px solid #3b82f650",
          borderRadius: 6,
          padding: "5px 10px",
          fontSize: 12,
          color: "#e2e8f0",
          pointerEvents: "none",
          zIndex: 9999,
          whiteSpace: "nowrap",
          boxShadow: "0 4px 16px #00000060",
        }}>{tooltip.text}</div>
      )}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&family=Playfair+Display:wght@700;900&display=swap');
        * { box-sizing: border-box; }
        button:hover { opacity: 0.85; }
        button:active { transform: scale(0.97); }
        button:focus-visible { outline: 2px solid #3b82f6; outline-offset: 2px; }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: mob ? 24 : 48 }}>
        <div style={{ fontSize: mob ? 9 : 11, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 10, fontFamily: "'IBM Plex Mono', monospace" }}>
          AI Foundations Team · AI Production Engine · March 2026
        </div>
        <h1 style={{ margin: "0 0 8px", fontSize: mob ? 24 : 42, fontWeight: 900, fontFamily: "'Playfair Display', Georgia, serif", color: "#f1f5f9", letterSpacing: "-0.02em", lineHeight: 1 }}>
          AI Production Engine
        </h1>
        <div style={{ fontSize: mob ? 12 : 15, color: "#475569", fontWeight: 400 }}>
          38 weeks · 6 phases · Live by November 2026 · Naviga contract expires February 2027
        </div>
      </div>

      {/* Phase timeline */}
      <div style={{ marginBottom: 40 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 14, fontFamily: "'IBM Plex Mono', monospace" }}>Phase Timeline</div>

        {!mob && (
          <div style={{ display: "flex", marginBottom: 6 }}>
            {PHASES.map(phase => {
              const widthPct = ((phase.weekRange[1] - phase.weekRange[0] + 1) / TOTAL_WEEKS) * 100;
              return (
                <div key={phase.id} style={{ width: `${widthPct}%`, paddingRight: 3 }}>
                  <div style={{ fontSize: 10, color: "#475569", fontFamily: "'IBM Plex Mono', monospace" }}>{phase.weeks}</div>
                  <div style={{ fontSize: 9, color: "#253347", fontFamily: "'IBM Plex Mono', monospace", marginTop: 1 }}>{phaseDateRange(phase.weekRange)}</div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: mob ? "grid" : "flex", gridTemplateColumns: mob ? "1fr 1fr" : undefined, gap: mob ? 8 : 3, marginBottom: 16 }}>
          {PHASES.map(phase => {
            const widthPct = ((phase.weekRange[1] - phase.weekRange[0] + 1) / TOTAL_WEEKS) * 100;
            const isActive = active === phase.id;
            return (
              <div
                key={phase.id}
                onClick={() => setSelectedPhase(selectedPhase === phase.id ? null : phase.id)}
                onMouseEnter={() => !selectedPhase && setHoveredPhase(phase.id)}
                onMouseLeave={() => !selectedPhase && setHoveredPhase(null)}
                style={{
                  width: mob ? undefined : `${widthPct}%`,
                  background: isActive ? phase.color : phase.colorDim,
                  borderRadius: 6,
                  padding: mob ? "10px 12px" : "14px 14px 12px",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  border: `1px solid ${isActive ? phase.color : "transparent"}`,
                  transform: isActive ? "translateY(-2px)" : "none",
                  boxShadow: isActive ? `0 8px 24px ${phase.color}30` : "none",
                }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: isActive ? "#fff" : phase.color, marginBottom: 4, fontFamily: "'IBM Plex Mono', monospace" }}>
                  {String(phase.id).padStart(2, "0")}
                </div>
                <div style={{ fontSize: mob ? 11 : 13, fontWeight: 700, color: isActive ? "#fff" : "#e2e8f0", lineHeight: 1.2 }}>
                  {phase.name}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{
          background: activePhase ? activePhase.colorDim : "#0f1623",
          border: `1px solid ${activePhase ? activePhase.color + "40" : "#1e2d42"}`,
          borderRadius: 8,
          padding: activePhase ? (mob ? "16px 14px" : "20px 24px") : (mob ? "12px 14px" : "14px 20px"),
          transition: "all 0.15s",
          minHeight: 80,
        }}>
          {activePhase ? (
            <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr" : "1fr 1fr 1fr", gap: mob ? 16 : 24 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: activePhase.color, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6, fontFamily: "'IBM Plex Mono', monospace" }}>Goal</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#f1f5f9", marginBottom: 8 }}>{activePhase.goal}</div>
                <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.6 }}>{activePhase.description}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: activePhase.color, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8, fontFamily: "'IBM Plex Mono', monospace" }}>Outputs</div>
                {activePhase.outputs.map((o, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6 }}>
                    <span style={{ color: activePhase.color, fontSize: 13, marginTop: 1, flexShrink: 0 }}>→</span>
                    <span style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.5 }}>{o}</span>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: activePhase.color, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8, fontFamily: "'IBM Plex Mono', monospace" }}>Milestone</div>
                <div style={{ fontSize: 14, color: "#f1f5f9", lineHeight: 1.5, padding: "10px 14px", background: activePhase.color + "15", borderRadius: 6, borderLeft: `3px solid ${activePhase.color}` }}>
                  {activePhase.milestone}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "#475569", fontStyle: "italic" }}>Click a phase to see detail</div>
          )}
        </div>
      </div>

      {/* Sprint Detail */}
      <div style={{ marginBottom: 40 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 18, fontFamily: "'IBM Plex Mono', monospace" }}>Sprint Detail</div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {PHASES.map(phase => {
            const isOpen = expandedPhase === phase.id;
            return (
              <div key={phase.id} style={{ border: `1px solid ${isOpen ? phase.color + "50" : "#1e2d42"}`, borderRadius: 8, overflow: "hidden", transition: "border-color 0.15s" }}>
                <div
                  onClick={() => setExpandedPhase(isOpen ? null : phase.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: mob ? 8 : 14, padding: mob ? "12px 12px" : "14px 18px",
                    background: isOpen ? phase.colorDim : "#0f1623",
                    cursor: "pointer", transition: "background 0.15s",
                    flexWrap: mob ? "wrap" : "nowrap",
                  }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: phase.color, fontFamily: "'IBM Plex Mono', monospace", background: phase.colorDim, padding: "2px 8px", borderRadius: 3, border: `1px solid ${phase.color}30` }}>
                    {String(phase.id).padStart(2, "0")}
                  </span>
                  <span style={{ fontSize: mob ? 12 : 13, fontWeight: 700, color: "#e2e8f0" }}>{phase.name}</span>
                  {!mob && <span style={{ fontSize: 12, color: "#475569" }}>{phase.weeks}</span>}
                  {!mob && <span style={{ fontSize: 12, color: "#334155" }}>·</span>}
                  {!mob && <span style={{ fontSize: 12, color: "#475569", fontFamily: "'IBM Plex Mono', monospace" }}>{phaseDateRange(phase.weekRange)}</span>}
                  {!mob && <span style={{ fontSize: 12, color: "#334155" }}>·</span>}
                  {!mob && <span style={{ fontSize: 12, color: "#475569" }}>{phase.sprints.length} sprints</span>}
                  <span style={{ marginLeft: "auto", fontSize: 12, color: phase.color, fontWeight: 600 }}>{isOpen ? "↑" : "↓"}</span>
                </div>
                {isOpen && (
                  <div style={{ padding: mob ? "12px 12px 14px" : "16px 18px 18px", background: "#070d1a" }}>
                    <SprintGrid phase={phase} mob={mob} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Team Capacity Ask By Phase */}
      <div style={{ marginBottom: 40 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 6, fontFamily: "'IBM Plex Mono', monospace" }}>Team Availability</div>
        <div style={{ fontSize: 12, color: "#475569", marginBottom: 18 }}>All team members assumed fully available except where marked. Hover a block for detail.</div>

        {(() => {
          const LEAVE = [
            { person: "Ben",   type: "leave",   label: "Annual leave",    start: new Date("2026-04-06"), end: new Date("2026-04-24") },
            { person: "Blair", type: "leave",   label: "Annual leave",    start: new Date("2026-04-22"), end: new Date("2026-05-03") },
            { person: "Ben",   type: "travel",  label: "INMA Berlin",     start: new Date("2026-05-04"), end: new Date("2026-05-08") },
            { person: "Keith", type: "travel",  label: "INMA Berlin",     start: new Date("2026-05-04"), end: new Date("2026-05-08") },
            { person: "Ben",   type: "leave",   label: "Annual leave",    start: new Date("2026-05-11"), end: new Date("2026-05-15") },
          ];

          const leaveColor   = "#ef444460";
          const leaveBorder  = "#ef4444";
          const travelColor  = "#f59e0b50";
          const travelBorder = "#f59e0b";

          function getBlockForWeek(person, weekNum) {
            const ws = weekStartDate(weekNum);
            const we = weekEndDate(weekNum);
            return LEAVE.find(l =>
              l.person === person &&
              ws <= l.end &&
              we >= l.start
            ) || null;
          }

          const weeks = Array.from({ length: TOTAL_WEEKS }, (_, i) => i + 1);

          return mob ? (
            /* Mobile: simplified team list with leave notes */
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {TEAM.map(member => {
                const memberLeave = LEAVE.filter(l => l.person === member.name);
                return (
                  <div key={member.name} style={{ background: "#0f1623", borderRadius: 8, border: "1px solid #1e2d42", padding: "12px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: memberLeave.length ? 8 : 0 }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: "50%",
                        background: member.color + "25",
                        border: `1px solid ${member.color}50`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 10, fontWeight: 700, color: member.color, flexShrink: 0,
                      }}>{member.avatar}</div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>{member.name}</div>
                        <div style={{ fontSize: 11, color: "#475569" }}>{member.role}</div>
                      </div>
                    </div>
                    {memberLeave.map((l, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, paddingLeft: 36 }}>
                        <span style={{ fontSize: 10, color: l.type === "leave" ? "#ef4444" : "#f59e0b", fontWeight: 700 }}>{l.type === "leave" ? "✕" : "✈"}</span>
                        <span style={{ fontSize: 12, color: "#64748b" }}>{l.label} · {fmtShort(l.start)} – {fmtShort(l.end)}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {/* Phase colour band */}
              <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", marginBottom: 4 }}>
                <div />
                <div style={{ display: "flex", gap: 2 }}>
                  {weeks.map(w => {
                    const phase = PHASES.find(p => w >= p.weekRange[0] && w <= p.weekRange[1]);
                    return (
                      <div key={w} style={{ flex: 1, height: 4, borderRadius: 2, background: phase ? phase.color + "60" : "#1e2d42" }} />
                    );
                  })}
                </div>
              </div>

              {/* Week number row */}
              <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", marginBottom: 8 }}>
                <div />
                <div style={{ display: "flex", gap: 2 }}>
                  {weeks.map(w => (
                    <div key={w} style={{ flex: 1, textAlign: "center" }}>
                      <span style={{ fontSize: 8, color: w % 4 === 1 ? "#475569" : "#253347", fontFamily: "'IBM Plex Mono', monospace" }}>{w % 4 === 1 ? w : "·"}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Team rows */}
              {TEAM.map((member, mi) => (
                <div key={member.name} style={{
                  display: "grid", gridTemplateColumns: "140px 1fr",
                  padding: "6px 0",
                  borderTop: mi === 0 ? "1px solid #1e2d42" : "1px solid #1a2438",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, paddingRight: 16 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: "50%",
                      background: member.color + "25",
                      border: `1px solid ${member.color}50`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 10, fontWeight: 700, color: member.color, flexShrink: 0,
                    }}>{member.avatar}</div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>{member.avatar}</div>
                      <div style={{ fontSize: 10, color: "#475569" }}>{member.role}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
                    {weeks.map(w => {
                      const block = getBlockForWeek(member.name, w);
                      const isLeave  = block && block.type === "leave";
                      const isTravel = block && block.type === "travel";
                      return (
                        <div
                          key={w}
                          onMouseEnter={e => {
                            const text = block
                              ? `${block.label}  ·  ${fmtShort(block.start)} – ${fmtShort(block.end)}`
                              : `Week ${w}  ·  ${fmtShort(weekStartDate(w))}`;
                            setTooltip({ text, x: e.clientX, y: e.clientY });
                          }}
                          onMouseLeave={() => setTooltip(null)}
                          style={{
                            flex: 1,
                            height: 28,
                            borderRadius: 3,
                            background: isLeave ? leaveColor : isTravel ? travelColor : member.color + "25",
                            border: `1px solid ${isLeave ? leaveBorder : isTravel ? travelBorder : member.color + "40"}`,
                            cursor: "default",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                          {isLeave  && <span style={{ fontSize: 9, color: "#ef4444", fontWeight: 700 }}>✕</span>}
                          {isTravel && <span style={{ fontSize: 9, color: "#f59e0b", fontWeight: 700 }}>✈</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Legend */}
              <div style={{ display: "flex", gap: 20, marginTop: 14, paddingLeft: 140 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 16, height: 16, borderRadius: 3, background: leaveColor, border: `1px solid ${leaveBorder}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: 9, color: "#ef4444", fontWeight: 700 }}>✕</span>
                  </div>
                  <span style={{ fontSize: 12, color: "#475569" }}>Annual leave</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 16, height: 16, borderRadius: 3, background: travelColor, border: `1px solid ${travelBorder}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: 9, color: "#f59e0b", fontWeight: 700 }}>✈</span>
                  </div>
                  <span style={{ fontSize: 12, color: "#475569" }}>Conference / travel</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 16, height: 16, borderRadius: 3, background: "#3b82f625", border: "1px solid #3b82f640" }} />
                  <span style={{ fontSize: 12, color: "#475569" }}>Fully available</span>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Hard Deadlines */}
      <div style={{ borderTop: "1px solid #1e2d42", paddingTop: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 14, fontFamily: "'IBM Plex Mono', monospace" }}>Hard Deadlines</div>
        <div style={{ display: "flex", flexDirection: mob ? "column" : "row", gap: 12 }}>
          {[
            { date: "Nov 2026", label: "System live", sub: "New authoring system in production", color: "#10b981" },
            { date: "Feb 2027", label: "Naviga contract expires", sub: "Full decommission of Naviga authoring", color: "#ef4444" },
          ].map(d => (
            <div key={d.date} style={{ flex: 1, padding: "16px 18px", background: "#0f1623", borderRadius: 8, borderLeft: `3px solid ${d.color}`, border: `1px solid ${d.color}25`, borderLeftWidth: 3 }}>
              <div style={{ fontSize: mob ? 16 : 18, fontWeight: 900, color: d.color, fontFamily: "'Playfair Display', Georgia, serif", marginBottom: 4 }}>{d.date}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0", marginBottom: 2 }}>{d.label}</div>
              <div style={{ fontSize: 12, color: "#475569" }}>{d.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Business Goals */}
      <div style={{ borderTop: "1px solid #1e2d42", paddingTop: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 20, fontFamily: "'IBM Plex Mono', monospace" }}>Business Success Measures</div>

        {/* Stuff Digital */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0", marginBottom: 12 }}>Stuff Digital</div>
          <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr" : "1fr 1fr 1fr", gap: 12 }}>
            {[
              {
                tag: "EBITDA",
                tagColor: "#f59e0b",
                title: "Reduce cost per published piece",
                description: "Exit circa $200k Naviga licensing cost (and business continuity risk) and produce more high-quality journalism in a wider range of formats without increasing FTE investment.",
              },
              {
                tag: "AUDIENCE",
                tagColor: "#3b82f6",
                title: "Enable consumer product innovation that grows audience",
                description: "Eliminate the constraint current editorial production workflows place on our ability to provide news experiences that keep pace with quickly changing consumer trends.",
              },
              {
                tag: "REVENUE",
                tagColor: "#10b981",
                title: "Retain ThreeNews production revenue",
                description: "Meaningfully reduce the cost to produce ThreeNews allowing us to strengthen quality and quantity of our service to Sky while increasing contract profitability.",
              },
            ].map(g => (
              <div key={g.title} style={{ padding: "16px 18px", background: "#0f1623", borderRadius: 8, border: "1px solid #1e2d42" }}>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", padding: "2px 7px", borderRadius: 3, color: g.tagColor, background: g.tagColor + "18", textTransform: "uppercase" }}>{g.tag}</span>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0", marginTop: 10, marginBottom: 8, lineHeight: 1.4 }}>{g.title}</div>
                <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>{g.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Stuff Masthead Publishing */}
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0", marginBottom: 12 }}>Stuff Masthead Publishing</div>
          <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr" : "1fr 1fr 1fr", gap: 12 }}>
            {[
              {
                tag: "EBITDA",
                tagColor: "#f59e0b",
                title: "Reduce cost per published piece",
                description: "Exit circa $533k Naviga licensing cost (and business continuity risk) and produce more high-quality journalism in a wider range of formats without increasing FTE investment.",
              },
              {
                tag: "EBITDA",
                tagColor: "#f59e0b",
                title: "Eliminate print production overhead",
                description: "Facilitate the full automation of print production, unlocking investment capacity for digital growth.",
              },
              {
                tag: "REVENUE",
                tagColor: "#10b981",
                title: "Enable consumer product innovation that grows subscriptions",
                description: "Eliminate the constraint current editorial production workflows place on our ability to provide news experiences that keep pace with quickly changing consumer trends.",
              },
            ].map(g => (
              <div key={g.title} style={{ padding: "16px 18px", background: "#0f1623", borderRadius: 8, border: "1px solid #1e2d42" }}>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", padding: "2px 7px", borderRadius: 3, color: g.tagColor, background: g.tagColor + "18", textTransform: "uppercase" }}>{g.tag}</span>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0", marginTop: 10, marginBottom: 8, lineHeight: 1.4 }}>{g.title}</div>
                <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>{g.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
